import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  FormControlLabel,
  Checkbox,
  LinearProgress,
  Typography,
  Box,
  Divider,
  TextField,
  Grid,
  Chip,
  Stack,
  IconButton,
  InputAdornment,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import {
  cancelEvent,
  fetchCancelJob,
  fetchCancelPreview,
} from '../../services/eventService';
import RefundNotificationFields from '../refunds/RefundNotificationFields';

const formatMoney = (n) =>
  `${Number(n || 0).toLocaleString('tr-TR', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })} TL`;

const JOB_STATUS_LABELS = {
  queued: 'Sırada',
  notifying: 'Bildirimler gönderiliyor',
  running: 'İadeler işleniyor',
  completed: 'Tamamlandı',
  partial: 'Kısmen tamamlandı',
  failed: 'Başarısız',
  cancelled: 'İptal edildi',
};

const isValidEmail = (value) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);

/** Devam eden (henüz bitmemiş) job durumları */
export const ACTIVE_JOB_STATUSES = ['queued', 'notifying', 'running'];

const NotifyListField = ({
  label,
  placeholder,
  inputType,
  draft,
  onDraftChange,
  items,
  onAdd,
  onRemove,
}) => {
  const handleAdd = () => {
    const value = draft.trim();
    if (!value) return;
    if (inputType === 'email' && !isValidEmail(value)) return;
    onAdd(value);
    onDraftChange('');
  };

  return (
    <Box>
      <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: 'block' }}>
        {label} ({items.length})
      </Typography>
      <TextField
        label={label}
        type={inputType}
        value={draft}
        onChange={(e) => onDraftChange(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            e.preventDefault();
            handleAdd();
          }
        }}
        fullWidth
        size="small"
        placeholder={placeholder}
        autoComplete="off"
        sx={{ mb: 1 }}
        InputProps={{
          endAdornment: (
            <InputAdornment position="end">
              <IconButton
                aria-label="ekle"
                edge="end"
                size="small"
                onClick={handleAdd}
                disabled={!draft.trim()}
                color="primary"
              >
                <AddIcon fontSize="small" />
              </IconButton>
            </InputAdornment>
          ),
        }}
      />
      {items.length > 0 && (
        <Stack direction="row" flexWrap="wrap" gap={0.75}>
          {items.map((item) => (
            <Chip
              key={item}
              label={item}
              size="small"
              onDelete={() => onRemove(item)}
            />
          ))}
        </Stack>
      )}
    </Box>
  );
};

/**
 * Etkinlik iptali onay diyaloğu + iade ilerleme takibi
 */
const CancelEventDialog = ({ open, eventId, eventName, onClose, onDone, notify }) => {
  const [loading, setLoading] = useState(false);
  const [preview, setPreview] = useState(null);
  const [reasonMail, setReasonMail] = useState('');
  const [reasonSms, setReasonSms] = useState('');
  const [reasonPush, setReasonPush] = useState('');
  const [sendMail, setSendMail] = useState(true);
  const [sendSms, setSendSms] = useState(true);
  const [sendPush, setSendPush] = useState(true);
  const [notifyEmails, setNotifyEmails] = useState([]);
  const [notifyPhones, setNotifyPhones] = useState([]);
  const [emailDraft, setEmailDraft] = useState('');
  const [phoneDraft, setPhoneDraft] = useState('');
  const [skipRefund, setSkipRefund] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [job, setJob] = useState(null);
  const wasOpenRef = useRef(false);

  useEffect(() => {
    if (!open || !eventId) {
      wasOpenRef.current = false;
      return undefined;
    }
    // Sekme / parent re-render'da formu sifirlamamak icin sadece acilista yukle
    const justOpened = !wasOpenRef.current;
    wasOpenRef.current = true;
    if (!justOpened) return undefined;

    let cancelled = false;
    setLoading(true);
    setJob(null);
    setNotifyEmails([]);
    setNotifyPhones([]);
    setEmailDraft('');
    setPhoneDraft('');
    setReasonMail('');
    setReasonSms('');
    setReasonPush('');
    setSendMail(true);
    setSendSms(true);
    setSendPush(true);
    setSkipRefund(false);
    (async () => {
      try {
        // Devam eden bir iptal job'u varsa (sayfadan çıkıp dönülmüş olabilir)
        // form yerine doğrudan ilerleme ekranını göster.
        const jobRes = await fetchCancelJob(eventId).catch(() => null);
        const activeJob = jobRes?.data;
        if (
          !cancelled &&
          activeJob &&
          ACTIVE_JOB_STATUSES.includes(activeJob.status)
        ) {
          setJob(activeJob);
          return;
        }
        const data = await fetchCancelPreview(eventId);
        if (!cancelled) setPreview(data);
      } catch (err) {
        if (!cancelled) {
          notify?.(err?.message || 'Önizleme alınamadı', { type: 'error' });
          onClose?.();
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
    // notify/onClose bilerek yok — her render'da sifirlamasin
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, eventId]);

  useEffect(() => {
    if (!job?.id || !eventId) return;
    if (['completed', 'partial', 'failed', 'cancelled'].includes(job.status)) {
      return undefined;
    }
    const timer = setInterval(async () => {
      try {
        const res = await fetchCancelJob(eventId);
        if (res?.data) setJob(res.data);
      } catch {
        /* ignore poll errors */
      }
    }, 3000);
    return () => clearInterval(timer);
  }, [job?.id, job?.status, eventId]);

  const handleConfirm = async () => {
    if (submitting || loading || job) return;
    setSubmitting(true);
    try {
      const emails = notifyEmails;
      const phones = notifyPhones;
      const res = await cancelEvent(eventId, {
        refund: !skipRefund,
        refundReasonMail: reasonMail.trim() || undefined,
        refundReasonSms: reasonSms.trim() || undefined,
        refundReasonPush: reasonPush.trim() || undefined,
        sendMail,
        sendSms,
        sendPush,
        notifyEmails: emails.length ? emails : undefined,
        notifyPhones: phones.length ? phones : undefined,
      });
      if (res?.jobId) {
        const latest = await fetchCancelJob(eventId);
        setJob(latest?.data || { id: res.jobId, status: 'queued', processed: 0, totalSales: res.totalSales || 0 });
        notify?.('Etkinlik iptal edildi. İadeler arka planda işleniyor.', {
          type: 'info',
        });
      } else {
        notify?.('Etkinlik iptal edildi.', { type: 'success' });
        onDone?.();
      }
    } catch (err) {
      notify?.(err?.message || 'İptal başarısız — etkinlik iptal edilmedi', {
        type: 'error',
      });
    } finally {
      setSubmitting(false);
    }
  };

  const jobDone =
    job &&
    ['completed', 'partial', 'failed', 'cancelled'].includes(job.status);

  const progress =
    job && job.totalSales > 0
      ? Math.min(100, Math.round((job.processed / job.totalSales) * 100))
      : job?.status === 'completed'
        ? 100
        : 0;

  const addEmail = useCallback((raw) => {
    const value = raw.trim().toLowerCase();
    if (!value || !isValidEmail(value)) {
      notify?.('Geçerli bir e-posta adresi girin', { type: 'warning' });
      return;
    }
    setNotifyEmails((prev) => (prev.includes(value) ? prev : [...prev, value]));
  }, [notify]);

  const addPhone = useCallback((raw) => {
    const value = raw.trim();
    if (!value) return;
    setNotifyPhones((prev) => (prev.includes(value) ? prev : [...prev, value]));
  }, []);

  const displayEventName = eventName || preview?.eventName || 'Bu etkinlik';
  const previewAmount = preview?.totalAmount;

  return (
    <Dialog open={open} onClose={job && !jobDone ? undefined : onClose} maxWidth="lg" fullWidth>
      <DialogTitle>Etkinliği iptal et</DialogTitle>
      <DialogContent>
        {loading && <LinearProgress />}
        {!loading && !job && preview && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
            <Typography>
              <strong>{displayEventName}</strong> iptal edilecek.
            </Typography>
            <Typography variant="body2" color="text.secondary">
              <strong>{preview.salesCount}</strong> satış /{' '}
              <strong>{preview.ticketCount}</strong> bilet /{' '}
              <strong>{formatMoney(preview.totalAmount)}</strong> iade edilecek.
              <br />
              <strong>{preview.distinctBuyers}</strong> kişiye seçilen kanallar
              (mail / SMS / push) üzerinden bildirim gidecek.
            </Typography>
            <Divider sx={{ my: 1 }} />
            <RefundNotificationFields
              key={`cancel-notify-${eventId}`}
              reasonMail={reasonMail}
              reasonSms={reasonSms}
              reasonPush={reasonPush}
              onMailChange={setReasonMail}
              onSmsChange={setReasonSms}
              onPushChange={setReasonPush}
              sendMail={sendMail}
              sendSms={sendSms}
              sendPush={sendPush}
              onSendMailChange={setSendMail}
              onSendSmsChange={setSendSms}
              onSendPushChange={setSendPush}
              eventName={displayEventName}
              isEventCancel
              previewAmount={previewAmount}
            />
            <Divider sx={{ my: 1 }} />
            <Box>
              <Typography variant="subtitle2" fontWeight={700} gutterBottom>
                Test / ek bildirim alıcıları
              </Typography>
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1.5 }}>
                Mail kapalıysa e-posta yazsan da mail gitmez; SMS açıksa aynı e-postaya
                (PUS’ta telefonu varsa) SMS gider. Garantili SMS için telefon da ekle.
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <NotifyListField
                    label="E-posta"
                    placeholder="sen@ornek.com"
                    inputType="email"
                    draft={emailDraft}
                    onDraftChange={setEmailDraft}
                    items={notifyEmails}
                    onAdd={addEmail}
                    onRemove={(item) =>
                      setNotifyEmails((prev) => prev.filter((e) => e !== item))
                    }
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <NotifyListField
                    label="Telefon (SMS)"
                    placeholder="05xx xxx xx xx"
                    inputType="tel"
                    draft={phoneDraft}
                    onDraftChange={setPhoneDraft}
                    items={notifyPhones}
                    onAdd={addPhone}
                    onRemove={(item) =>
                      setNotifyPhones((prev) => prev.filter((p) => p !== item))
                    }
                  />
                </Grid>
              </Grid>
            </Box>
            <FormControlLabel
              control={
                <Checkbox
                  checked={skipRefund}
                  onChange={(e) => setSkipRefund(e.target.checked)}
                />
              }
              label="İade yapma, sadece iptal et"
            />
          </Box>
        )}
        {job && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, pt: 1 }}>
            <Typography variant="body2">
              Durum: <strong>{JOB_STATUS_LABELS[job.status] || job.status}</strong>
            </Typography>
            <LinearProgress variant="determinate" value={progress} />
            <Typography variant="body2">
              {job.processed || 0} / {job.totalSales || 0} satış işlendi
              {job.succeeded != null ? ` · başarılı: ${job.succeeded}` : ''}
              {job.failed ? ` · hatalı: ${job.failed}` : ''}
            </Typography>
            {jobDone && (
              <Typography variant="body2" color="text.secondary">
                Toplam iade: {formatMoney(job.totalRefunded)} · Mail:{' '}
                {job.mailSent ?? 0} · SMS: {job.smsSent ?? 0} · Push:{' '}
                {job.pushSent ?? 0}
              </Typography>
            )}
            {job.lastError && (
              <Typography variant="body2" color="error">
                {job.lastError}
              </Typography>
            )}
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        {!job && (
          <>
            <Button
              type="button"
              onClick={onClose}
              disabled={submitting}
            >
              Vazgeç
            </Button>
            <Button
              type="button"
              color="error"
              variant="contained"
              onClick={handleConfirm}
              disabled={loading || submitting}
            >
              {skipRefund ? 'Sadece iptal et' : 'İptal et ve iade başlat'}
            </Button>
          </>
        )}
        {jobDone && (
          <Button
            variant="contained"
            onClick={() => {
              onDone?.();
              onClose?.();
            }}
          >
            Tamam
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default CancelEventDialog;
